/** Chat screen rules that do not need the browser (tested in tests/unit/web-chat.test.ts). */
import type { MessageKey } from './i18n.ts';

/** The assistant function's error codes → what the owner reads. */
export function chatErrorKey(code: string | null): MessageKey {
  switch (code) {
    case 'not_owner':
      return 'chatErrNotOwner';
    case 'ai_not_configured':
      return 'chatErrNotConfigured';
    case 'ai_budget':
      return 'chatErrBudget';
    case 'network':
      return 'chatErrNetwork';
    default:
      return 'chatErrGeneric';
  }
}

/** Trial data is flagged for every answer that stood on it, fully or partly. */
export function isTrial(dataSource: string | null): boolean {
  return dataSource === 'mock' || dataSource === 'mixed';
}

export interface ChatMessage {
  readonly key: string;
  /** The stored answer's id (cards are saved against it); null for a question not yet answered. */
  readonly messageId: number | null;
  readonly role: 'user' | 'assistant';
  readonly content: string;
  readonly createdAt: string;
  readonly dataSource: string | null;
  /**
   * The answer stood on Google numbers while the ads account was not linked; null when not
   * known (answers stored before this was recorded, when the account was never linked).
   */
  readonly notLinked: boolean | null;
  readonly inputMode: string | null;
  readonly cards: unknown;
}

/**
 * The chat's trial banner for the latest answer: Google numbers while the ads account is not
 * linked (also for older answers, from before it was recorded), or, with the account linked,
 * the ratings and reviews only. Null when the latest answer stood on no trial data.
 */
export function trialBannerFor(
  messages: readonly ChatMessage[],
): 'chatTrial' | 'chatTrialReviews' | null {
  const lastAnswer = [...messages].reverse().find((m) => m.role === 'assistant');
  if (lastAnswer === undefined || !isTrial(lastAnswer.dataSource)) return null;
  return lastAnswer.notLinked === false ? 'chatTrialReviews' : 'chatTrial';
}

/** Whether a stored answer's tools stood on Google numbers while not linked (null: not recorded). */
export function toolsNotLinked(tools: unknown): boolean | null {
  if (!Array.isArray(tools) || tools.length === 0) return null;
  const flags = tools.map((t) =>
    typeof t === 'object' && t !== null ? (t as Record<string, unknown>)['notLinked'] : undefined,
  );
  if (!flags.every((f) => typeof f === 'boolean')) return null;
  return flags.some((f) => f);
}

/** Grows the composer with its text, up to a limit (then it scrolls). */
export function composerRows(text: string, max = 6): number {
  const lines = text.split('\n').length;
  return Math.min(max, Math.max(1, lines));
}

/** Key of one card under one answer, as saved decisions record it. */
export const cardKey = (messageId: number, cardIndex: number): string =>
  `${String(messageId)}:${String(cardIndex)}`;
